import React, { useEffect, useState } from "react";
import { IconButton, Box, Typography, Link } from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  LoginSubmitBtn,
  LoginContainer,
  LoginFormContent,
  LoginFormWrapper,
  LoginWrapper,
  Title,
  SubTitle,
  InputLogin,
  LoginForm,
  LoginFieldset,
  LabelCheckbox,
  InputWrapper,
  InputIconLeft,
  InputIconRight,
  BrandPanel,
  Spinner,
} from "./LoginFields.styled";

import menuImg from "../../assets/imgs/logoSaudePlenaMoinhos.svg";
import { ReactComponent as EmailSuccess } from "../../assets/imgs/emailSuccess.svg";
import { Auth } from "../../core/http/service/Auth";
import AuthDTO from "../../models/auth/AuthDTO";
import { useAppDispatch } from "../../index";
import * as loginActions from "../../store/actions/login";
import { UserDataTypes } from "../../store/actions/login";
import SuccessModal from "../successModal/SuccessModal";
import ErrorArea from "../error/ErrorArea";
import { getBackendErrorList } from "../../helpers/HelperFunctions";
import { mountUserDataPostLogin } from "../../helpers/LoginHelperFunctions";

function LoginFields() {
  const [cpf, setCpf] = useState<string>("");
  const [cpfChangePass, setCpfChangePass] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [loginErrors, setLoginError] = useState<any[]>([]);
  const [passRetrieveErrors, setPassRetrieveErrors] = useState([]);
  const [showModalEmailSent, setShowModalEmailSent] = useState(false);
  const [isChangePasswordState, setIsChangePasswordState] = useState(false);
  const [keepConnected, setKeepConnected] = useState<boolean>(true);
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();

  function maskcpf(v: any, op: number) {
    setTimeout(() => {
      v = v.replace(/\D/g, "");
      v = v.replace(/(\d{3})(\d)/, "$1.$2");
      v = v.replace(/(\d{3})(\d)/, "$1.$2");
      v = v.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
      op === 1 ? setCpf(v) : setCpfChangePass(v);
    }, 1);
  }

  async function _onSubmit(
    e: React.FormEvent<HTMLFormElement>,
    cpfVal: string,
    passwordVal: string,
    keepConn: boolean,
    dispatchFn: any
  ) {
    e.preventDefault();
    const auth = new Auth();
    const data = new AuthDTO().fromJSON({
      user: cpfVal,
      password: passwordVal,
    });

    try {
      setLoading(true);

      if (keepConn) {
        localStorage.setItem("cpf", cpfVal);
        localStorage.setItem("senha", passwordVal);
      } else {
        localStorage.removeItem("cpf");
        localStorage.removeItem("senha");
      }

      const loginResponse: any = await auth.postAuth(data);
      const userData: UserDataTypes = mountUserDataPostLogin(
        cpfVal,
        passwordVal,
        loginResponse,
        keepConn,
        []
      );

      await dispatchFn(loginActions.login(userData));
      setLoginError([]);
    } catch (error) {
      console.error("Erro durante o login:", error);
      setLoginError(getBackendErrorList(error));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const cpfSalvo = localStorage.getItem("cpf");
    const senhaSalva = localStorage.getItem("senha");

    if (cpfSalvo && senhaSalva) {
      setCpf(cpfSalvo);
      setPassword(senhaSalva);
      setKeepConnected(true);
    }
  }, []);

  async function _onSubmitChangePassword(
    e: React.FormEvent<HTMLFormElement>,
    cpfVal: string,
    emailVal: string,
    dispatchFn: any
  ) {
    e.preventDefault();
    try {
      setLoading(true);
      await dispatchFn(loginActions.retrievePassword(cpfVal, emailVal));
      setShowModalEmailSent(true);
      setPassRetrieveErrors([]);
    } catch (error: any) {
      setPassRetrieveErrors(getBackendErrorList(error));
    } finally {
      setLoading(false);
    }
  }

  return (
    <LoginWrapper>
      <LoginContainer>
        {/* Left Column: Form Section */}
        <LoginFormWrapper>
          {!isChangePasswordState ? (
            <LoginFormContent>
              <Title>Bem-vindo de volta!</Title>
              <SubTitle>
                Acesse sua conta para gerenciar atendimentos e dados de saúde.
              </SubTitle>

              <LoginForm
                onSubmit={(e) => {
                  if (password === "" && cpf === "") {
                    e.preventDefault();
                    setLoginError([
                      { message: "Digite um usuário/CPF" },
                      { message: "Digite uma senha" },
                    ]);
                  } else if (cpf === "") {
                    e.preventDefault();
                    setLoginError([{ message: "Digite um usuário/CPF" }]);
                  } else if (password === "") {
                    e.preventDefault();
                    setLoginError([{ message: "Digite uma senha" }]);
                  } else {
                    setLoginError([]);
                    _onSubmit(e, cpf, password, keepConnected, dispatch);
                  }
                }}
              >
                <LoginFieldset>
                  {/* CPF Input */}
                  <InputWrapper>
                    <InputIconLeft>
                      <BadgeOutlinedIcon fontSize="small" />
                    </InputIconLeft>
                    <InputLogin
                      type="text"
                      value={cpf}
                      onChange={(e) => maskcpf(e.target.value, 1)}
                      placeholder="CPF / Usuário"
                      maxLength={14}
                    />
                  </InputWrapper>

                  {/* Password Input */}
                  <InputWrapper>
                    <InputIconLeft>
                      <LockOutlinedIcon fontSize="small" />
                    </InputIconLeft>
                    <InputLogin
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      type={showPassword ? "text" : "password"}
                      placeholder="Sua senha"
                    />
                    <InputIconRight>
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword(!showPassword)}
                        sx={{ color: "#6b7280" }}
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputIconRight>
                  </InputWrapper>

                  {loginErrors.length > 0 && (
                    <ErrorArea
                      errorList={loginErrors}
                      onCloseAction={() => setLoginError([])}
                    />
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mt: 1,
                    }}
                  >
                    <LabelCheckbox>
                      <input
                        type="checkbox"
                        checked={keepConnected}
                        onChange={(e) => setKeepConnected(e.target.checked)}
                      />
                      Mantenha-me conectado
                    </LabelCheckbox>

                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => {
                        setLoginError([]);
                        setIsChangePasswordState(true);
                      }}
                      sx={{
                        color: "#0A5190",
                        fontWeight: 600,
                        textDecoration: "none",
                        fontSize: "13.5px",
                        fontFamily: "Inter, sans-serif",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      Esqueceu sua senha?
                    </Link>
                  </Box>
                </LoginFieldset>

                <LoginSubmitBtn type="submit" disabled={loading} $loading={loading}>
                  {loading ? <Spinner /> : "Entrar no Sistema"}
                </LoginSubmitBtn>
              </LoginForm>
            </LoginFormContent>
          ) : (
            <LoginFormContent>
              <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => setIsChangePasswordState(false)}
                  sx={{ color: "#0A5190", p: 0.5 }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{ color: "#0A5190", fontWeight: 700, cursor: "pointer" }}
                  onClick={() => setIsChangePasswordState(false)}
                >
                  Voltar para o Login
                </Typography>
              </Box>

              <Title>Recuperação de Senha</Title>
              <SubTitle>
                Insira o seu CPF e e-mail associado. Enviaremos as instruções para a recuperação da sua senha.
              </SubTitle>

              <LoginForm
                onSubmit={(e) =>
                  _onSubmitChangePassword(e, cpfChangePass, email, dispatch)
                }
              >
                <LoginFieldset>
                  <InputWrapper>
                    <InputIconLeft>
                      <BadgeOutlinedIcon fontSize="small" />
                    </InputIconLeft>
                    <InputLogin
                      type="text"
                      value={cpfChangePass}
                      onChange={(e) => maskcpf(e.target.value, 2)}
                      placeholder="Digite seu CPF"
                    />
                  </InputWrapper>

                  <InputWrapper>
                    <InputIconLeft>
                      <EmailOutlinedIcon fontSize="small" />
                    </InputIconLeft>
                    <InputLogin
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      type="email"
                      placeholder="Digite seu e-mail cadastrado"
                    />
                  </InputWrapper>

                  {passRetrieveErrors.length > 0 && (
                    <ErrorArea
                      errorList={passRetrieveErrors}
                      onCloseAction={() => setPassRetrieveErrors([])}
                    />
                  )}
                </LoginFieldset>

                <LoginSubmitBtn type="submit" disabled={loading} $loading={loading}>
                  {loading ? <Spinner /> : "Enviar Instruções"}
                </LoginSubmitBtn>
              </LoginForm>
            </LoginFormContent>
          )}
        </LoginFormWrapper>

        {/* Right Column: Brand Branding Panel */}
        <BrandPanel>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              textAlign: "center",
            }}
          >
            <img
              src={menuImg}
              style={{ width: "280px", maxWidth: "100%", height: "auto" }}
              alt="Logo Saúde Plena Moinhos"
            />
            <Box
              sx={{
                width: 40,
                height: 3,
                bgcolor: "#FF8310",
                borderRadius: 2,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: "#4B5563",
                fontWeight: 600,
                fontSize: "14px",
                maxWidth: 260,
                lineHeight: 1.5,
                fontFamily: "Inter, sans-serif",
              }}
            >
              Plataforma Integrada de Saúde, Prevenção e Promoção da Saúde
            </Typography>
          </Box>
        </BrandPanel>
      </LoginContainer>

      <SuccessModal
        Icon={() => <EmailSuccess />}
        modalMsg="Link para a troca da senha enviado por e-mail. O link expira em uma hora!"
        visible={showModalEmailSent}
        onVoltar={() => {
          setIsChangePasswordState(false);
          setShowModalEmailSent(false);
        }}
      />
    </LoginWrapper>
  );
}

export default LoginFields;
